import Vue from 'vue'
// @ts-ignore
import NoSsr from 'vue-no-ssr'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { JobApplicationStatuses } from '@/constants/job/jobApplicationStatuses'
import { META_COUNTRIES } from '@/constants/meta'
import { BROWSE_JOBS, BROWSE_JOBS_BY_SKILL, JOB_ADD, JOB_DETAILS, JOB_DETAILS_BY_ID } from '@/constants/routes'
import { DATE_TIME_FORMAT_WITHOUT_SEC } from '@/constants/utils/date'
import canonicalMixin from '@/mixins/canonicalMixin'
import responseMixin from '@/mixins/responseMixin'
import shareableMixin from '@/mixins/shareableMixin'
import snackMixin from '@/mixins/snackMixin'
import unreadMessagesBlinkTitleMixin from '@/mixins/unreadMessagesBlinkTitleMixin'
import JobApplication from '@/models-ts/job/JobApplication'
import JobCard from '@/partials/JobCards/JobCard/JobCard.vue'
import UserInfo from '@/partials/UserInfo/UserInfo.vue'
import { googleAnalyticsV2 } from '@/servicies-ts/analytics'
import { RootState } from '@/store'
import { stripDescriptionTags } from '@/utils-ts/strings'
import { addMonths, formatDate, getHumanDate } from '@/utils/date'
import ErrorMatcher from '@/utils/ErrorMatcher'
import { formatUsd } from '@/utils/moneyFormat'
import { parseTemplate } from '@/utils/parser'
import ActionsCard from '../components/ActionsCard/ActionsCard.vue'
import JobDescription from '../components/JobDescription/JobDescription.vue'
import MobileActionsCard from '../components/MobileActionsCard/MobileActionsCard.vue'
import ShareBlock from '../components/ShareBlock/ShareBlock.vue'
import SignUpCard from '../components/SignUpCard/SignUpCard.vue'

export default Vue.extend<any, any, any, any>({
  mixins: [responseMixin, shareableMixin, snackMixin, canonicalMixin, unreadMessagesBlinkTitleMixin],
  components: {
    ActionsCard,
    MobileActionsCard,
    JobDescription,
    NoSsr,
    JobCard,
    ShareBlock,
    SignUpCard,
    UserInfo,
  },
  data() {
    return {
      BROWSE_JOBS_BY_SKILL,
      swipers: [],
      starting: false,
      mobileMoreSkillsOpened: false,
      shouldClearJob: true,
    }
  },
  computed: {
    ...mapState<RootState>({
      isLoggedIn: (state: RootState) => state.app.authorized,
      prefetched: (state: RootState) => state.jobDetails.prefetched,
      profiles: (state: RootState) => state.user?.profiles,
    }),
    ...mapGetters({
      job: 'jobDetails/job',
      jobId: 'jobDetails/jobId',
      isLoading: 'jobDetails/isLoading',
      isLoaded: 'jobDetails/isLoaded',
      isOwner: 'jobDetails/isCustomer',
      isMember: 'jobDetails/isMember',
    }),
    breadcrumbs() {
      return [
        { title: 'Crypto Jobs', to: { name: BROWSE_JOBS } },
        this.category?.name
          ? { title: this.category.name, to: { name: BROWSE_JOBS_BY_SKILL, params: { skill: this.category.url } } }
          : null,
        { title: this.job ? this.job.name : 'Job' },
      ].filter(Boolean)
    },
    inArchive() {
      return this.job && !this.job.isNotStarted && !this.isMember
    },
    hasSignUpCard() {
      return this.inArchive && !this.isLoggedIn
    },
    skills() {
      const skills = this.job.relations.Skill.filter((skill: any) => skill.id !== this.category?.id).map((skill: any) => skill.name)
      return skills
    },
    skillsTags() {
      let categorySkill = null
      if (this.category) {
        categorySkill = {
          text: this.category.name,
          link: {
            name: BROWSE_JOBS_BY_SKILL,
            params: { skill: this.category.url },
          },
        }
      }
      const skills: Array<CloudyTag> = this.job.relations.Skill.filter((skill: any) => skill.id !== this.category?.id).map(
        (skill: any) => ({
          text: skill.name,
          link: {
            name: BROWSE_JOBS_BY_SKILL,
            params: { skill: skill.url },
            ...(!skill.is_custom && skill.url && { link: { name: BROWSE_JOBS_BY_SKILL, params: { skill: skill.url } } }),
          },
        })
      )
      if (categorySkill) {
        skills.unshift(categorySkill)
      }
      return skills
    },
    userJobs() {
      return this.job.more.byUser
    },
    categoryJobs() {
      return this.job.more.byCategory
    },
    name() {
      return this.job?.name
    },
    category() {
      return this.job.meta.category
    },
    budget() {
      if (!this.job) {
        return ''
      }
      return this.isMember && this.job.wasStarted ? this.job.formattedBudget : `$${formatUsd(this.job.budget)}`
    },
    customer() {
      return this.job?.relations.Customer
    },
    unreadCount() {
      if (this.isOwner && this.isLoggedIn) {
        const applications = (this.job.applications || []).filter((app: JobApplication) => app.status === JobApplicationStatuses.NEW)
        const count = applications.reduce((count: number, app: JobApplication) => (!app.isRead ? count + 1 : count), 0)
        return count > 99 ? '99+' : count
      }
    },
    title() {
      if (!this.name) {
        return 'Loading'
      }
      return `${this.name} - freelance job${this.category ? ` in ${this.category.name}` : ''}, ${this.budget} price in cryptocurrency`
    },
    metaDescription() {
      if (!this.name) {
        return ''
      }
      // eslint-disable-next-line max-len
      return `Apply for ${this.name} job${this.category ? ` in ${this.category.name}` : ''}, Fixed price ${this.budget} in cryptocurrency. It takes just a few clicks!`
    },
  },
  watch: {
    '$route.params.id': {
      handler(newJob, oldJob) {
        if (newJob && String(newJob) !== String(oldJob)) {
          this.clearJob()
          this.load({ id: newJob, slug: this.$route.params.slug })
        }
      },
    },
    isLoaded: {
      handler() {
        if (this.isLoaded) {
          this.$nextTick(async () => {
            this.sendAnalytics()
            // @ts-ignore
            const Swiper = (await import(/* webpackChunkName: "swiper" */ 'swiper')).default
            ;['userJobs', 'categoryJobs'].forEach((ref) => {
              if (this.$refs[ref]) {
                const swiper = new Swiper(this.$refs[ref], {
                  slidesPerView: 'auto',
                  spaceBetween: 32,
                  freeMode: true,
                  navigation: {
                    nextEl: this.$refs[`${ref}NextBtn`].$el,
                    prevEl: this.$refs[`${ref}PrevBtn`].$el,
                  },
                })
                swiper.allowTouchMove = false
                this.swipers.push(swiper)
              }
            })
          })
        }
      },
      immediate: true,
    },
  },
  async prefetch() {
    try {
      if (process.server) {
        if (!this.isLoggedIn) {
          await this.load({ id: this.$route.params.id, slug: this.$route.params.slug })
          this.setPrefetched(true)
        } else {
          this.$store.commit('responseHeaders/disableCacheControl')
        }
      }
      if (this.$route.name === JOB_DETAILS_BY_ID && this.job) {
        this.setRedirect(parseTemplate(JOB_DETAILS, { id: this.job.id, slug: this.job.slug }))
      }
    } catch (e) {
      if (ErrorMatcher.isGone(e) || ErrorMatcher.isNotFound(e) || ErrorMatcher.isForbidden(e) || ErrorMatcher.isConflict(e)) {
        this.setNotFound(true)
      } else {
        console.error('Fetch job details error:', e)
      }
    }
  },
  async created() {
    try {
      if (process.client) {
        if (!this.prefetched) {
          if (String(this.job?.id) !== String(this.jobId)) {
            await this.load({ id: this.$route.params.id, slug: this.$route.params.slug })
          }
          if (this.$route.name === JOB_DETAILS_BY_ID) {
            this.$router.replace({ name: JOB_DETAILS, params: { id: this.job.id, slug: this.job.slug } })
          }
        } else {
          this.setPrefetched(false)
        }
      }
    } catch (e) {
      if (ErrorMatcher.isGone(e) || ErrorMatcher.isNotFound(e) || ErrorMatcher.isForbidden(e) || ErrorMatcher.isConflict(e)) {
        this.setNotFound(true)
      } else {
        this.parseError(e)
      }
    }
  },
  beforeDestroy() {
    if (!this.jobId && this.shouldClearJob) {
      this.clearJob()
    }
  },
  methods: {
    getHumanDate,
    ...mapMutations({
      setNotFound: 'app/setNotFound',
      setRedirect: 'app/setRedirect',
      setPrefetched: 'jobDetails/setPrefetched',
      clearJob: 'jobDetails/clearJob',
    }),
    ...mapActions({
      load: 'jobDetails/load',
    }),
    onClickSkill(skill: any) {
      googleAnalyticsV2.send({
        event: 'gig-click-on-skill',
        name: skill.text,
        job_id: this.job.id,
      })
    },
    sendAnalytics() {
      googleAnalyticsV2.send({
        event: 'freel-job-view',
        job_id: this.job.id,
      })
      googleAnalyticsV2.send({
        event: 'detailView',
        job_id: this.job.id,
        ecommerce: {
          detail: {
            actionField: {
              list: this.$route.query.fromUrl || '',
            },
            products: [
              {
                name: this.name,
                id: `${this.job.id}`,
                price: this.budget,
                category: this.$route.query.skill || this.category?.name || '',
                brand: 'Jobs',
              },
            ],
          },
        },
      })
    },
    onClickPostJob() {
      this.shouldClearJob = false
      this.$router.push({ name: JOB_ADD, query: { fromType: 'job', jobId: this.job.id } })
    },
  },
  metaInfo() {
    const meta: Array<any> = [
      {
        vmid: 'description',
        name: 'description',
        content: this.metaDescription,
      },
    ]
    const script = []
    if (this.job) {
      const getBudgetUnit = (budget: number) => {
        if (budget < 50) {
          return 'HOUR'
        } else if (budget < 200) {
          return 'DAY'
        } else if (budget < 1000) {
          return 'WEEK'
        }
        return 'MONTH'
      }
      meta.push(
        {
          vmid: 'og:title',
          property: 'og:title',
          content: 'New job on LaborX.com',
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: `Job Title: ${this.name}`,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: this.shareUrl,
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: `${process.env.VUE_APP_FRONTEND_URL}/static/images/laborx-thumbnail.jpg`,
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: 'website',
        }
      )
      if (!this.inArchive) {
        script.push({
          type: 'application/ld+json',
          json: {
            '@context': 'http://schema.org',
            '@type': 'JobPosting',
            title: this.name,
            description: stripDescriptionTags(this.job.description, { stripLinks: true }),
            datePosted: formatDate(this.job.created_at, 'YYYY-MM-DD'),
            employmentType: 'CONTRACTOR',
            jobLocationType: 'TELECOMMUTE',
            applicantLocationRequirements: META_COUNTRIES.map((name) => ({
              '@type': 'Country',
              name,
            })),
            hiringOrganization: {
              '@type': 'Organization',
              name: this.customer.name,
              ...(this.customer.avatar?.src && { logo: this.customer.avatar.src }),
              ...(this.customer.website && { sameAs: this.customer.website }),
            },
            ...(this.category && { occupationalCategory: this.category.name }),
            ...(this.skills.length && { skills: this.skills.join(', ') }),
            estimatedSalary: {
              '@type': 'MonetaryAmount',
              currency: 'USD',
              value: {
                '@type': 'QuantitativeValue',
                value: this.job.budget,
                unitText: getBudgetUnit(Number(this.job.budget)),
              },
            },
            validThrough: formatDate(addMonths(this.job.published_at, 6), DATE_TIME_FORMAT_WITHOUT_SEC),
          },
        })
      }
    }
    return {
      title: this.getMetaTitle(this.title),
      meta,
      script,
      link: [this.canonicalLink],
    }
  },
})
